"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OnboardingTourLauncher = void 0;
const intercom_tour_1 = require("@app/component/pages/onboarding/util/intercom-tour");
const onboarding_1 = require("@app/reducer/onboarding");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const use_onboarding_1 = require("@app/util/hooks/use-onboarding");
const react_1 = __importDefault(require("react"));
const react_router_dom_1 = require("react-router-dom");
/** how long to wait for the tour's anchor to be drawn before starting the tour anyway */
const ANCHOR_TIMEOUT_MS = 5000;
/** how often to look for it in the meantime */
const ANCHOR_POLL_MS = 100;
/**
 * Starts the Intercom product tour of the onboarding step the manager just left the
 * checklist to do.
 *
 * Must be mounted OUTSIDE the router's <Switch> (Routes.js): the checklist page unmounts the
 * moment „Vyřešit" navigates away, so it cannot wait for the page the tour is actually about.
 *
 * A tour starts only when all of these hold, which is what keeps it from firing at the
 * wrong moment:
 *
 *  - the route CHANGED (never on the first render) — otherwise a page reload, which restores
 *    the persisted `activeStep`, would replay the tour every time the manager refreshes;
 *  - `activeStep` says the manager is currently doing that step — the store is the only
 *    thing that knows this, and it is what tells the schedule and publish tours apart, since
 *    they live on the same path;
 *  - `tourPending` says the step was ENTERED by clicking „Vyřešit" and its tour has not run
 *    since. Without it the tour would reopen on every later visit to the page: `activeStep`
 *    survives until the step is genuinely finished, and a manager who started the publish
 *    step is then one navigation away from the schedule — which is where the app lands by
 *    default. Clicking „Vyřešit" again sets it back and replays the tour, which is what
 *    `REPLAY_TOURS` is about;
 *  - the new path is that step's own page;
 *  - the checklist still applies at all (`useOnboarding().isEligible`).
 */
const OnboardingTourLauncher = () => {
    const dispatch = (0, store_1.useRootDispatch)();
    const { pathname } = (0, react_router_dom_1.useLocation)();
    const { workspaceId, isEligible } = (0, use_onboarding_1.useOnboarding)();
    const flags = (0, store_1.useRootSelector)((state) => (0, util_1.isValue)(workspaceId) ? state.onboarding[workspaceId] : undefined);
    const activeStep = flags?.activeStep;
    const isTourPending = flags?.tourPending === true;
    /** null until the first render has been seen, so the initial mount never counts as a change */
    const previousPathname = react_1.default.useRef(null);
    react_1.default.useEffect(() => {
        const previous = previousPathname.current;
        previousPathname.current = pathname;
        if (!(0, util_1.isValue)(previous) || previous === pathname)
            return;
        if (!isEligible || !(0, util_1.isValue)(workspaceId))
            return;
        if (!(0, util_1.isValue)(activeStep) || !isTourPending)
            return;
        const tour = intercom_tour_1.TOURS[activeStep];
        if ((0, util_1.isUndefined)(tour) || !pathname.startsWith(tour.path))
            return;
        const open = () => {
            (0, intercom_tour_1.startTour)(activeStep);
            dispatch((0, onboarding_1.startedOnboardingTour)(workspaceId));
        };
        const { anchor } = tour;
        if ((0, util_1.isUndefined)(anchor)) {
            open();
            return;
        }
        // The page the manager just landed on renders (and often loads) after this effect, so
        // the anchor is usually not there yet. Give up after a while and start the tour anyway:
        // Intercom drops steps whose target is missing, so the rest of the tour still runs —
        // which is what should happen for an anchor that is conditionally rendered, like the
        // publish button that only appears once there are unpublished changes.
        //
        // setTimeout rather than requestAnimationFrame: frames stop entirely in a background
        // tab, so a manager who switches away mid-load would come back to a tour opening
        // minutes after the navigation that asked for it.
        let timer;
        let isWaiting = true;
        const startedAt = performance.now();
        const lookForAnchor = () => {
            const found = (0, util_1.isValue)(document.querySelector(`[data-tour="${anchor}"]`));
            if (found || performance.now() - startedAt > ANCHOR_TIMEOUT_MS) {
                if (!found) {
                    console.warn(`onboarding-tour-launcher: anchor "${anchor}" never appeared, starting the tour without it`);
                }
                isWaiting = false;
                open();
                return;
            }
            timer = setTimeout(lookForAnchor, ANCHOR_POLL_MS);
        };
        timer = setTimeout(lookForAnchor, 0);
        // The manager left before the anchor appeared, so this tour is not happening. Spend
        // „Vyřešit" anyway: `tourPending` is persisted and `activeStep` outlives the visit, so
        // leaving it set would open the tour on whatever later, unrelated navigation happens to
        // land on this page next - possibly days afterwards. Clicking „Vyřešit" again is what
        // asks for it a second time.
        return () => {
            clearTimeout(timer);
            if (isWaiting)
                dispatch((0, onboarding_1.startedOnboardingTour)(workspaceId));
        };
    }, [pathname, activeStep, isTourPending, isEligible, workspaceId, dispatch]);
    return null;
};
exports.OnboardingTourLauncher = OnboardingTourLauncher;
exports.default = exports.OnboardingTourLauncher;
